/* eslint-disable no-unused-vars */
import { ArrowUpRight, Link2, Unlink, UserRound } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import type { WorkJourneyItemRow, WorkJourneyRow } from '../api';
import { StatusChip } from './Avatar';

type Props = {
  journey: WorkJourneyRow;
  taskItems: WorkJourneyItemRow[];
  onCreate: (
    _sourceWorkJourneyItemId: string,
    _targetWorkJourneyItemId: string
  ) => Promise<boolean>;
  onDelete: (_relationshipId: string) => Promise<void>;
};

function itemKey(item: WorkJourneyItemRow) {
  return String(item.metadata?.['identifier'] || item.externalRefId);
}

function relatedKey(item: {
  externalRefId: string;
  metadata?: Record<string, unknown>;
}) {
  return String(item.metadata?.['identifier'] || item.externalRefId);
}

function ownerSummary(
  item: Pick<WorkJourneyItemRow, 'metadata' | 'responsibilities'>
) {
  const resolvedOwners = (item.responsibilities || [])
    .filter((responsibility) => responsibility.role === 'assignee')
    .map(
      (responsibility) =>
        responsibility.teamHandle ||
        (responsibility.identity?.isCurrentUser
          ? 'You'
          : responsibility.identity?.displayName ||
            responsibility.identity?.handle)
    )
    .filter((value): value is string => Boolean(value));
  if (resolvedOwners.length) return [...new Set(resolvedOwners)].join(', ');

  const assignees = item.metadata?.['assignees'];
  if (!Array.isArray(assignees)) return 'Owner not reported';
  if (!assignees.length) return 'Unassigned';
  const names = assignees.flatMap((value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
    const person = value as Record<string, unknown>;
    const label = [
      person['displayName'],
      person['name'],
      person['username'],
      person['handle'],
      person['email']
    ].find((candidate) => typeof candidate === 'string' && candidate.trim());
    return typeof label === 'string' ? [label.trim()] : [];
  });
  if (!names.length)
    return `${assignees.length} ${assignees.length === 1 ? 'assignee' : 'assignees'} · name not provided`;
  const unnamedCount = assignees.length - names.length;
  return `${names.join(', ')}${unnamedCount ? ` +${unnamedCount} without a name` : ''}`;
}

function TaskOwner({
  item
}: {
  item: Pick<WorkJourneyItemRow, 'metadata' | 'responsibilities'>;
}) {
  return (
    <span className="journeyTaskOwner">
      <UserRound size={12} aria-hidden="true" />
      {ownerSummary(item)}
    </span>
  );
}

export function WorkItemRelationshipsPanel({
  journey,
  taskItems,
  onCreate,
  onDelete
}: Props) {
  const [linkingItemId, setLinkingItemId] = useState('');
  const [query, setQuery] = useState('');
  const taskById = useMemo(
    () => new Map(taskItems.map((item) => [item.workJourneyItemId, item])),
    [taskItems]
  );
  const localTasks = journey.items
    .filter((item) => item.kind === 'task')
    .map((item) => taskById.get(item.workJourneyItemId) || item);
  const candidates = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (needle.length < 2) return [];
    return taskItems
      .filter((item) => item.kind === 'task')
      .filter((item) =>
        `${item.provider} ${itemKey(item)} ${item.title}`
          .toLowerCase()
          .includes(needle)
      );
  }, [query, taskItems]);

  if (!localTasks.length) return null;

  return (
    <section className="journeyRelatedTasks">
      <div className="journeyInspectorHeading">
        <div>
          <span>Task relationships</span>
          <strong>Planning and implementation work</strong>
        </div>
      </div>
      <p>
        Link tasks that belong together. Each task keeps its own status and
        owner.
      </p>
      <div className="journeyRelatedTaskList">
        {localTasks.map((task) => {
          const linkedIds = new Set(
            (task.relationships || [])
              .filter(
                (relationship) => relationship.direction === 'implemented_by'
              )
              .map((relationship) => relationship.relatedItem.workJourneyItemId)
          );
          const eligibleCandidates = candidates
            .filter(
              (candidate) =>
                candidate.workJourneyItemId !== task.workJourneyItemId &&
                !linkedIds.has(candidate.workJourneyItemId)
            )
            .slice(0, 8);
          return (
            <div className="journeyRelatedTask" key={task.workJourneyItemId}>
              <div className="journeyRelatedTaskSource">
                <span>
                  {task.provider} · {itemKey(task)}
                </span>
                <strong>{task.title}</strong>
                <TaskOwner item={task} />
                <StatusChip tone={statusTone(task.status)}>
                  {task.status}
                </StatusChip>
                {task.url ? (
                  <a
                    href={task.url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`Open ${task.title}`}
                  >
                    <ArrowUpRight size={13} />
                  </a>
                ) : null}
              </div>
              <div className="journeyRelatedTaskChildren">
                {(task.relationships || []).map((relationship) => (
                  <div key={relationship.workJourneyItemRelationshipId}>
                    <span>
                      {relationship.direction === 'implemented_by'
                        ? 'Implemented by'
                        : 'Implements'}
                    </span>
                    <strong>
                      {relationship.relatedItem.provider} ·{' '}
                      {relatedKey(relationship.relatedItem)} ·{' '}
                      {relationship.relatedItem.title}
                    </strong>
                    <TaskOwner
                      item={
                        taskById.get(
                          relationship.relatedItem.workJourneyItemId
                        ) || relationship.relatedItem
                      }
                    />
                    <StatusChip
                      tone={statusTone(relationship.relatedItem.status)}
                    >
                      {relationship.relatedItem.status}
                    </StatusChip>
                    {relationship.relatedItem.url ? (
                      <a
                        href={relationship.relatedItem.url}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={`Open ${relationship.relatedItem.title}`}
                      >
                        <ArrowUpRight size={13} />
                      </a>
                    ) : null}
                    <button
                      type="button"
                      className="textButton"
                      onClick={() =>
                        void onDelete(
                          relationship.workJourneyItemRelationshipId
                        )
                      }
                      aria-label={`Remove link to ${relationship.relatedItem.title}`}
                    >
                      <Unlink size={13} />
                      Remove link
                    </button>
                  </div>
                ))}
                {linkingItemId === task.workJourneyItemId ? (
                  <div className="journeyRelatedTaskSearch">
                    <label>
                      <span>Find an execution task</span>
                      <input
                        autoFocus
                        type="search"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder="Search task key or title"
                      />
                    </label>
                    {query.trim().length < 2 ? (
                      <small>Type at least two characters.</small>
                    ) : eligibleCandidates.length ? (
                      eligibleCandidates.map((candidate) => (
                        <button
                          type="button"
                          key={candidate.workJourneyItemId}
                          onClick={() => {
                            void onCreate(
                              task.workJourneyItemId,
                              candidate.workJourneyItemId
                            ).then((created) => {
                              if (created) {
                                setLinkingItemId('');
                                setQuery('');
                              }
                            });
                          }}
                        >
                          <span>
                            {candidate.provider} · {itemKey(candidate)}
                          </span>
                          <strong>{candidate.title}</strong>
                          <TaskOwner item={candidate} />
                          <StatusChip tone={statusTone(candidate.status)}>
                            {candidate.status}
                          </StatusChip>
                        </button>
                      ))
                    ) : (
                      <small>No tracked tasks match that search.</small>
                    )}
                    <button
                      type="button"
                      className="textButton"
                      onClick={() => {
                        setLinkingItemId('');
                        setQuery('');
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="textButton journeyAddTaskLink"
                    onClick={() => {
                      setLinkingItemId(task.workJourneyItemId);
                      setQuery('');
                    }}
                  >
                    <Link2 size={14} />
                    Add execution task
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function statusTone(
  status: WorkJourneyItemRow['status']
): 'warn' | 'success' | 'neutral' {
  if (status === 'failed' || status === 'blocked') return 'warn';
  if (status === 'success') return 'success';
  return 'neutral';
}
